"use client";

import useResizeObserver from "@react-hook/resize-observer";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef } from "react";

import { postIframeData } from "@/lib";
import { Box } from "@/storybook-components";
import type { ReactNode } from "react";
import HiddenGoogleTranslate from "@/components/google-translate/hidden";

interface EmbedsLayoutProps {
  children: ReactNode;
  [key: string]: any;
}

const EmbedsLayout = ({ children, ...rest }: EmbedsLayoutProps) => {
  const searchParams = useSearchParams();
  const hostOrigin = searchParams?.get("hostOrigin") || "";
  const rafRef = useRef<number | null>(null);
  const lastHeightRef = useRef<number | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const postHeight = useCallback(() => {
    if (!hostOrigin || !process.browser) return;

    // Cancel any pending frame to prevent excessive calls
    if (rafRef.current) window.cancelAnimationFrame(rafRef.current);

    rafRef.current = window.requestAnimationFrame(() => {
      const content = contentRef.current;
      if (!content) return;

      void content.offsetHeight;
      const height = Math.ceil(
        Math.max(
          content.scrollHeight,
          content.offsetHeight,
          content.getBoundingClientRect().height
        )
      );

      // Skip transient (0) and unchanged heights to avoid redundant
      // postMessage churn. The resulting height is identical, so this does not
      // change behaviour for legacy or new listeners.
      if (height <= 0 || height === lastHeightRef.current) return;
      lastHeightRef.current = height;

      postIframeData({
        hostOrigin,
        height,
        location: document.location.href
      });
    });
  }, [hostOrigin]);

  useResizeObserver(
    hostOrigin && process.browser ? contentRef : null,
    postHeight
  );

  useEffect(() => {
    if (!hostOrigin || !process.browser) return;

    const observer = new window.MutationObserver(postHeight);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    };
  }, [hostOrigin, postHeight]);

  return (
    <Box as="main" ref={contentRef} {...rest}>
      <HiddenGoogleTranslate />
      {children}
    </Box>
  );
};

export default EmbedsLayout;
